import { PROVIDER_LABELS_TH, ROLE_LABELS, type Provider } from '@natashu/shared';
import {
  Bell,
  BellOff,
  BellRing,
  ChevronDown,
  ChevronRight,
  KeyRound,
  LogOut,
  Menu,
  MessageCircleReply,
  Moon,
  Search,
  Share2,
  ShieldCheck,
  Sun,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { useNavigate } from 'react-router';
import { Avatar } from '../components/ui/Avatar.tsx';
import { IconButton } from '../components/ui/Button.tsx';
import { DropdownMenu, type MenuEntry } from '../components/ui/DropdownMenu.tsx';
import { ProviderIcon } from '../components/ui/ProviderIcon.tsx';
import { RelativeTime } from '../components/ui/RelativeTime.tsx';
import { Badge } from '../components/ui/StatusPill.tsx';
import { useChatAlertPrefs } from '../features/chat-alerts/prefs.ts';
import { useNeedsReplyPreview } from '../features/chat-alerts/useNeedsReplyPreview.ts';
import { useRecentShares } from '../features/chat-alerts/useRecentShares.ts';
import { GuideButton } from '../features/guide/GuideButton.tsx';
import { READ_CONVERSATIONS, useNeedsReplyCount } from '../features/inbox/counts.ts';
import type { InboxRow } from '../features/inbox/types.ts';
import { PresenceMenu } from '../features/presence/PresenceMenu.tsx';
import { useAuth } from '../lib/auth.tsx';
import { useTheme } from '../lib/theme.ts';
import type { RouteHandle } from './nav.ts';
import { RealtimeIndicator } from './RealtimeIndicator.tsx';
import { usePageMeta } from './pageMeta.tsx';

export interface TopbarProps {
  handle: RouteHandle | undefined;
  showMenuButton: boolean;
  menuExpanded: boolean;
  onOpenMenu: () => void;
  onOpenPalette: () => void;
}

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

function BellChatRow({ row }: { row: InboxRow }) {
  const preview = row.lastMessage?.preview?.trim() || 'ส่งรูปภาพหรือไฟล์';
  return (
    <span className="bell-chat" data-testid="bell-chat">
      <ProviderIcon provider={row.channel.provider} size={28} demoTag={false} />
      <span className="bell-chat__main">
        <span className="bell-chat__name truncate">{row.contact.displayName}</span>
        <span className="bell-chat__preview">{preview}</span>
        <span className="bell-chat__meta">
          {PROVIDER_LABELS_TH[row.channel.provider as Provider] ?? row.channel.provider}
          {row.channel.isDemo && row.channel.provider !== 'mock' ? ' (DEMO)' : ''} · {row.channel.name} · <RelativeTime value={row.lastMessage?.at ?? row.lastActivity.at} />
        </span>
      </span>
    </span>
  );
}

export function Topbar({ handle, showMenuButton, menuExpanded, onOpenMenu, onOpenPalette }: TopbarProps) {
  const { me, logout, can } = useAuth();
  const { description: pageDescription } = usePageMeta();
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const themeLabel = theme === 'dark' ? 'เปลี่ยนเป็นโหมดสว่าง' : 'เปลี่ยนเป็นโหมดมืด';
  const canReadChats = can(READ_CONVERSATIONS);
  // The bell shows chats waiting for a reply (the latest ones are listed in its menu).
  const needsReply = useNeedsReplyCount();
  const unread = needsReply ?? 0;
  const preview = useNeedsReplyPreview(canReadChats && unread > 0);
  const { prefs, update } = useChatAlertPrefs();
  const roleLabel = me ? (ROLE_LABELS[me.user.role]?.th ?? me.user.role) : '';
  const soundLabel = 'ปิดเสียงแจ้งเตือนแชท';

  /**
   * "ให้มีแจ้งเตือนที่ระบบแชร์โพสต์" (owner, 30 ก.ย. 2569). A team-post share happens on its own — the shop used
   * to find out only by looking at the Page — so the last week's shares sit in the bell beside the chats, and
   * the ones it has not seen yet count towards the badge.
   */
  const shares = useRecentShares(canReadChats);
  const chatRows = unread > 0 ? (preview.data ?? []) : [];
  const shareItems: Array<MenuEntry & { at: number }> = shares.items.slice(0, 3).map((s) => ({
    at: Date.parse(s.at),
    key: `share-${s.id}`,
    label: (
      <span className="bell-chat">
        <Share2 className={s.error ? 'bell-chat__icon bell-chat__icon--error' : 'bell-chat__icon'} size={16} strokeWidth={1.75} aria-hidden="true" />
        <span className="bell-chat__main">
          {s.error ? (
            <>
              <span className="bell-chat__name bell-chat__name--error">
                {s.failedStep === 'share' ? 'แชร์โพสต์รายชื่อทีมไม่สำเร็จ' : 'อัปเดตโพสต์รายชื่อทีมไม่สำเร็จ'}
                {s.monthLabelTh ? ` · ${s.monthLabelTh}` : ''}
              </span>
              <span className="bell-chat__preview bell-chat__preview--error">
                {s.failedStep === 'share' ? 'โพสต์อัปเดตแล้ว แต่แชร์ขึ้นหน้าเพจไม่ได้ — ' : ''}
                {s.error}
              </span>
            </>
          ) : (
            <>
              <span className="bell-chat__name">แชร์โพสต์รายชื่อทีมแล้ว{s.monthLabelTh ? ` · ${s.monthLabelTh}` : ''}</span>
              <span className="bell-chat__preview">
                {s.batch ? `รวบ ${s.changes ?? 0} การแก้ไขในครั้งเดียว` : 'รายชื่อทีมเปลี่ยน จึงแชร์ให้'}
                {s.teams === null ? '' : ` · ตอนนี้ ${s.teams.toLocaleString('th-TH')} ทีม`}
              </span>
            </>
          )}
          <span className="bell-chat__meta">
            <RelativeTime value={s.at} />
            {s.error ? ' · กดเพื่อดูที่หน้าใบสมัคร' : s.permalink ? ' · กดเพื่อเปิดโพสต์' : ''}
          </span>
        </span>
      </span>
    ),
    // A failure takes the shop to where the post is set up, not to a post that may not have changed.
    onSelect: () => (s.error || !s.permalink ? navigate('/registrations') : window.open(s.permalink, '_blank', 'noopener')),
  }));
  // One list, newest first, chats and share notices together (owner, 5 ต.ค. 2569: "เรียงตามปกตินี่แหละ ล่าสุดอยู่บนเรื่อยๆ").
  // v1.79.0 put the shares on top of everything and v1.88.0's first try put them under everything; time is the rule.
  const chatItems: Array<MenuEntry & { at: number }> = chatRows.map((row) => ({
    at: Date.parse(row.lastMessage?.at ?? row.lastActivity.at),
    key: `chat-${row.id}`,
    label: <BellChatRow row={row} />,
    onSelect: () => navigate(`/inbox/${row.id}`),
  }));
  const newestFirst = [...chatItems, ...shareItems].sort((a, b) => (Number.isNaN(b.at) ? 0 : b.at) - (Number.isNaN(a.at) ? 0 : a.at)).map(({ at: _at, ...entry }) => entry as MenuEntry);
  const bellItems: MenuEntry[] = [
    ...(newestFirst.length > 0
      ? newestFirst
      : [{ key: 'empty', label: unread > 0 ? 'กำลังโหลดแชทรอตอบ…' : 'ยังไม่มีการแจ้งเตือนใหม่', icon: BellOff, disabled: true, onSelect: () => undefined }]),
    { type: 'separator', key: 'sep' },
    ...(unread > 0
      ? [{ key: 'needs-reply', label: `ดูแชทรอตอบทั้งหมด (${unread > 99 ? '99+' : unread})`, icon: MessageCircleReply, onSelect: () => navigate('/inbox?view=needs_reply') }]
      : []),
    ...(canReadChats
      ? [{ key: 'sound', label: prefs.sound ? 'ปิดเสียงแจ้งเตือนแชท' : 'เปิดเสียงแจ้งเตือนแชท', icon: prefs.sound ? VolumeX : Volume2, onSelect: () => update({ sound: !prefs.sound }) }]
      : []),
    { key: 'alert-settings', label: 'ตั้งค่าการแจ้งเตือนแชท', icon: BellRing, onSelect: () => navigate('/account/notifications') },
  ];

  const bellCount = unread + shares.unseen;
  const bellTitle =
    shares.unseen > 0 && unread > 0
      ? `แชทรอตอบ ${unread > 99 ? '99+' : unread} รายการ · แชร์โพสต์ ${shares.unseen}`
      : shares.unseen > 0
        ? `แชร์โพสต์รายชื่อทีม ${shares.unseen} ครั้ง`
        : unread > 0
          ? `แชทรอตอบ ${unread > 99 ? '99+' : unread} รายการ`
          : 'การแจ้งเตือน';

  return (
    <header className="topbar">
      <div className="topbar__left">
        {showMenuButton && (
          <IconButton
            icon={Menu}
            label="เปิดเมนูหลัก"
            tooltip={false}
            aria-expanded={menuExpanded}
            aria-controls="nav-drawer"
            onClick={onOpenMenu}
            className="topbar__menu"
          />
        )}
        <div className="topbar__titles">
          <div className="topbar__crumbs">
            {handle?.group && (
              <>
                <span className="topbar__group">{handle.group}</span>
                <ChevronRight className="topbar__sep" size={14} strokeWidth={1.75} aria-hidden="true" />
              </>
            )}
            <span className="topbar__page">{handle?.title ?? 'NATASHU'}</span>
          </div>
          {pageDescription && (
            <p className="topbar__desc" title={pageDescription}>
              {pageDescription}
            </p>
          )}
        </div>
        {handle?.guideKey && <GuideButton guideKey={handle.guideKey} />}
      </div>

      <div className="topbar__right">
        <button type="button" className="topbar-search" onClick={onOpenPalette} aria-haspopup="dialog" aria-keyshortcuts="Control+K Meta+K" aria-label="ค้นหาหรือไปยังหน้า">
          <Search size={18} strokeWidth={1.75} aria-hidden="true" />
          <span className="topbar-search__text" aria-hidden="true">
            ค้นหาหรือไปยังหน้า…
          </span>
          <kbd className="topbar-search__kbd" aria-hidden="true">
            {IS_MAC ? '⌘ K' : 'Ctrl K'}
          </kbd>
        </button>

        <RealtimeIndicator />
        <PresenceMenu />

        {canReadChats && (
          <IconButton
            icon={prefs.sound ? Volume2 : VolumeX}
            label={soundLabel}
            aria-pressed={!prefs.sound}
            onClick={() => update({ sound: !prefs.sound })}
            className="topbar__sound"
          />
        )}

        <DropdownMenu
          label="การแจ้งเตือน"
          minWidth={300}
          header={<p className="topbar-menu__title">{bellTitle}</p>}
          items={bellItems}
          trigger={(props) => (
            <IconButton
              {...props}
              icon={Bell}
              label={bellCount > 0 ? `การแจ้งเตือน ${bellCount} รายการใหม่` : 'การแจ้งเตือน'}
              count={bellCount}
              tooltip={false}
              // Opening the bell is what counts as having seen the shares listed inside it.
              onClick={() => {
                shares.markSeen();
                props.onClick();
              }}
            />
          )}
        />

        <IconButton icon={theme === 'dark' ? Sun : Moon} label={themeLabel} onClick={toggle} className="topbar__theme" />

        {me && (
          <DropdownMenu
            label="เมนูบัญชี"
            minWidth={250}
            header={
              <div className="account-menu__header">
                <p className="account-menu__name">{me.user.displayName}</p>
                <p className="account-menu__email">{me.user.email}</p>
                <Badge tone="brand" size="sm">
                  {roleLabel}
                </Badge>
              </div>
            }
            items={[
              {
                key: 'mfa',
                label: me.user.mfaEnabled ? 'การยืนยัน 2 ชั้น (เปิดอยู่)' : 'ตั้งค่า 2FA',
                icon: ShieldCheck,
                onSelect: () => navigate('/account/security'),
              },
              { key: 'password', label: 'เปลี่ยนรหัสผ่าน', icon: KeyRound, onSelect: () => navigate('/account/password') },
              { key: 'notifications', label: 'การแจ้งเตือนแชท', icon: BellRing, onSelect: () => navigate('/account/notifications') },
              { key: 'theme', label: themeLabel, icon: theme === 'dark' ? Sun : Moon, onSelect: toggle },
              { type: 'separator', key: 'sep' },
              { key: 'logout', label: 'ออกจากระบบ', icon: LogOut, danger: true, onSelect: () => void logout() },
            ]}
            trigger={(props) => (
              <button {...props} type="button" className="topbar-user" aria-label={`เมนูบัญชี ${me.user.displayName}`}>
                <Avatar name={me.user.displayName} size={36} />
                <span className="topbar-user__text" aria-hidden="true">
                  <span className="topbar-user__name">{me.user.displayName}</span>
                  <span className="topbar-user__role">{roleLabel}</span>
                </span>
                <ChevronDown className="topbar-user__chevron" size={16} strokeWidth={1.75} aria-hidden="true" />
              </button>
            )}
          />
        )}
      </div>
    </header>
  );
}
